<template>
  <Html :lang="htmlAttrs.lang" :dir="htmlAttrs.dir">

  <Body class="bg-white">
    <div class="min-h-screen bg-white">
      
      <div id="body-content"></div>
      <Header />
      <VSnackBarToast />
      <div class="mt-[80px]"></div>
      <!-- Search box tag - hidden on mobile -->
      <div id="search-box-tag" class="hidden md:block">
        <div class="catagory-dropdown" v-if="productsMenu.length || brandImages.length">
          
          <!-- Products Menu - Styled like custom.vue -->
          <div class="products-dropdown print:hidden shadow-md my-2 bg-white border-y border-gray-100 w-full" v-if="productsMenu.length">
            <div class="grid grid-cols-12 gap-4 px-4">
              <div class="col-start-1 col-span-12 flex! flex-row! flex-nowrap! items-center">
                <div class="cat-tree p-2 shrink-0!">
                  <CategoryCategoryTree />
                </div>
                <div class="all-items all-items-deals green-nav-height flex flex-1 flex-wrap justify-center items-center gap-2 p-2">
                  <div class="catagory-items-sub-info catagory-item-sub" v-for="pm in productsMenu" :key="pm.key">
                    <p v-if="!privateProductsMenus[pm.key] || (privateProductsMenus[pm.key] && isAuthenticated)"
                      class="catagory-text flex items-center cursor-pointer px-4 py-2 rounded-md hover:bg-gray-50 text-gray-700 hover:text-primary transition-all duration-200" 
                      @click="addFilter(pm)">
                      <img :src="image(pm.img, { height: 20 })" class="w-5 h-5 mr-2 object-contain grayscale group-hover:grayscale-0" alt="icon" />
                      <span class="font-semibold uppercase tracking-wide text-xs">{{ $t(`menu.${pm.key}`) }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Brands Swiper -->
          <div class="container mx-auto px-2 my-4">
            <div class="catagory-brands bg-white p-4 rounded-lg shadow-sm border border-gray-100 min-h-24">
              <template v-if="brandImages.length">
                <template v-if="shouldRenderBrandSwiper">
                  <swiper-container :watchSlidesProgress="true" :slidesPerView="12" :navigation="false" :grabCursor="true"
                    :autoplay="{
                      delay: 1500,
                      disableOnInteraction: false,
                    }" :freeMode="true" :centeredSlides="false" :modules="modules" class="mySwiper swiper">
                    <swiper-slide v-for="bImg in brandImages" :key="bImg.slug" class="swiper-slide flex justify-center items-center h-16">
                      <NuxtLinkLocale :to="bImg.url" class="catagory-brands-sub-info block opacity-60 hover:opacity-100 transition-opacity" aria-label="Image-Link">
                        <img :src="imgUrl(bImg.image.url)" class="cld-logo-chip img-info-brands w-12 md:w-16 h-auto object-contain max-h-12" alt="Image" loading="lazy" decoding="async" width="64" height="48" />
                      </NuxtLinkLocale>
                    </swiper-slide>
                  </swiper-container>
                </template>
                <template v-else>
                  <!-- Static row until Swiper is ready (prevents CLS) -->
                  <div class="flex items-center gap-6 h-16 overflow-hidden">
                    <NuxtLinkLocale v-for="bImg in brandImages.slice(0, 12)" :key="bImg.slug" :to="bImg.url"
                      class="block opacity-60 hover:opacity-100 transition-opacity shrink-0" aria-label="Image-Link">
                      <img :src="imgUrl(bImg.image.url)" class="cld-logo-chip w-12 md:w-16 h-auto object-contain max-h-12" alt="Brand" loading="lazy" decoding="async" width="64" height="48" />
                    </NuxtLinkLocale>
                  </div>
                </template>
              </template>
              <template v-else>
                <!-- Skeleton reserved space until brand data arrives (prevents CLS) -->
                <div class="flex items-center gap-6 h-16 overflow-hidden">
                  <div v-for="n in 12" :key="n" class="w-16 h-10 bg-gray-100 rounded animate-pulse shrink-0"></div>
                </div>
              </template>
            </div>
          </div>

        </div>
        <div v-else class="h-52 w-full bg-gray-100 animate-pulse relative overflow-hidden">
          <div class="shimmer-stripe"></div>
        </div>
      </div>

      <!-- Container with MiniSearch -->
      <div v-if="!route.meta.hideSearch" class="container mx-auto px-3 md:px-4">
        <Suspense>
          <template #default>
            <MiniSearch :range="15"></MiniSearch>
          </template>
          <template #fallback>
            <div class="w-full bg-gray-100 animate-pulse h-10">
            </div>
          </template>
        </Suspense>
      </div>

      <NuxtPage />

      <LazyFooter />
  
    </div>
  </Body>

  </Html>
</template>

<script setup>
import { getQuery, parseURL } from "ufo";

import { shallowRef } from 'vue';
const modules = shallowRef([]);
const swipersReady = ref(false);
import CategoryCategoryTree from '../components/Category/TreeNavigation.vue';
import { useCategoryStore } from "~/store/CategoryStore";
import { useUtilityStore } from "~/store/UtililityStore";
import { useCmsStore } from "~/store/cms/cmsStore";
import { useI18n } from "vue-i18n";
import { useUserStore } from "~/store/UserStore";
import { productsMenu, privateProductsMenus } from "~/composables/data";
import { useFilterStore } from "~/store/FilterStore";
import { useOrderStore } from "~/store/OrderStore";

const { t, locale } = useI18n();

const head = useLocaleHead({
  addDirAttribute: true,      // Adds dir
  addSeoAttributes: true,     // Adds lang
})
const htmlAttrs = computed(() => head.value.htmlAttrs)
useHead({
  title: t('seo.title'),// "CLD Distribution - Leading European Distributor and Wholesaler in Entertainment and Pop-Culture ",
  meta: [{ name: "description", content: t('cld_desc1') }],
  link: head.value.link,
  htmlAttrs: head.value.htmlAttrs,
});


const image = useImage()
const cmsStore = useCmsStore();
const filterStore = useFilterStore();
const categoryStore = useCategoryStore();
const utilityStore = useUtilityStore();
const userStore = useUserStore();
const orderStore = useOrderStore();
const selectedLanguage = computed(() => utilityStore.selectedLanguage);
const brandImages = computed(() => cmsStore.brandImages);
const parentCategories = computed(() => categoryStore.parentCategories);
const localePath = useLocalePath();
const parentCategoriesObj = computed(() => {
  return {
    ...Object.assign(
      {},
      ...parentCategories.value.map((x) => {
        return {
          [x.key]: { ...x },
        };
      })
    ),
  };
});
const isAuthenticated = computed(() => userStore.isAuthenticated);

const shouldRenderBrandSwiper = computed(() => process.client && swipersReady.value);

const runWhenIdle = (fn) => {
  if (!process.client) return;
  const ric = window.requestIdleCallback;
  if (typeof ric === 'function') {
    ric(() => fn(), { timeout: 2000 });
  } else {
    setTimeout(() => fn(), 150);
  }
};
const route = useRoute();
const router = useRouter();
const imgUrl = (url) => cmsStore.getImageUrl(url);
const addFilter = async (v) => {
  
  const rawPath = typeof v?.path === "string" ? v.path : "/products/search";
  const { pathname } = parseURL(rawPath);
  const query = getQuery(rawPath);

  const targetPath = localePath(pathname || "/products/search");
  const isOnSearchPage = route.path === targetPath;

  // First redirect (only if needed)
  if (!isOnSearchPage) {
    await router.push({ path: targetPath, query });
  } else {
    // Same page: just update query without a full navigation history entry
    await router.replace({ path: targetPath, query });
  }


}
onBeforeMount(() => {
  setTimeout(() => {
    categoryStore.getParentCategories();
    cmsStore.getLicense();
    cmsStore.getCoverSliders();
    //cmsStore.getCmsCategory();
    cmsStore.getCmsSlider();

  }, 0);
});

// Fetch brand images early (SSR-safe now that cmsStore guards localStorage)
useAsyncData('layout-cms-brand', async () => {
  try {
    await cmsStore.getCmsBrand();
  } catch (e) {
    // Swallow to avoid breaking layout; skeleton will remain.
  }
  return true;
});

onMounted(() => {
  runWhenIdle(async () => {
    try {
      const m = await import('swiper/modules');
      modules.value = [m.FreeMode, m.Autoplay];
      swipersReady.value = true;
    } catch (e) {
      swipersReady.value = false;
    }
  });
});

useAsyncData('init_cms_cache', async () => {
  return cmsStore.initFromCache();
}, {
  lazy: true,
  server: false,
});

onMounted(() => {
  setTimeout(() => {
    orderStore.getAccountingSituationLocalStorage()
  }, 2000);
})

useAsyncData('deliverable-orders', async () => {
  return orderStore.getDeliverablesTotalFromStorage('default layout');
}, {
  lazy: true,
  server: false,

})
</script>
<style>
/* Shimmer animation for loading states */
.shimmer-stripe {
  position: absolute;
  top: 0;
  left: -150px;
  width: 150px;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.5) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmerMove 1.3s infinite;
}

@keyframes shimmerMove {
  0% {
    left: -150px;
  }
  100% {
    left: 100%;
  }
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .catagory-dropdown .cat-tree {
    max-height: 220px;
    overflow-y: auto;
  }

  .catagory-items .all-items {
    display: block;
  }

  .catagory-text {
    font-size: 0.875rem;
  }

  .catagory-brands .swiper-slide {
    width: auto !important;
  }
}

@media (max-width: 420px) {
  .catagory-text {
    font-size: 13px;
  }
}
</style>
